import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import { RefreshCw, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getMySyncLog } from '../../services/api';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const Pagination = ({ page, pages, total, limit, onPageChange }) => {
  if (total <= limit) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <span className="text-xs text-slate-500">{from}-{to} de {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 px-2 py-1">{page}/{pages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const SyncLog = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (direction) params.direction = direction;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await getMySyncLog(params);
      setData(res.data);
    } catch { toast.error('Error cargando log de sincronización'); }
    finally { setLoading(false); }
  }, [page, direction, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const entityCounts = (item) => {
    const entities = item.entities_synced || item.entities || {};
    if (typeof entities === 'object' && !Array.isArray(entities)) {
      return Object.entries(entities).map(([k, v]) => `${k}: ${v}`).join(', ') || '\u2014';
    }
    return '\u2014';
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <RefreshCw className="w-7 h-7 text-teal-600" />
          Sync Log
        </h1>
        <p className="text-slate-500 mt-1">Registro de sincronizaciones POS de tu negocio</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 items-center">
          <select value={direction} onChange={e => { setDirection(e.target.value); setPage(1); }}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Todas las direcciones</option>
            <option value="push">Push</option>
            <option value="pull">Pull</option>
          </select>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Desde</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Hasta</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          {(direction || dateFrom || dateTo) && (
            <button onClick={() => { setDirection(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline">Limpiar</button>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Producto</th>
                <th className="text-center px-4 py-3">Dirección</th>
                <th className="text-left px-4 py-3">Entidades</th>
                <th className="text-right px-4 py-3">Errores</th>
                <th className="text-left px-4 py-3">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400">No hay registros de sincronización</td></tr>
              )}
              {data.items.map((item, idx) => (
                <tr key={item.id || idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-700">{item.product_code || '\u2014'}</td>
                  <td className="px-4 py-3 text-center">
                    {item.direction === 'push' ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Push</span>
                    ) : item.direction === 'pull' ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Pull</span>
                    ) : '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{entityCounts(item)}</td>
                  <td className="px-4 py-3 text-right">
                    {item.errors_count > 0 ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">{item.errors_count}</span>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(item.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </DashboardLayout>
  );
};

export default SyncLog;
